<script lang="ts">
  import type {Language, Poem, Tag, User} from '../../types'
  import {lang, replaceValues, t} from '../../i18n'
  import {modal} from '../../stores/modal'
  import UsersModal from './UsersModal.svelte'
  import TranslationsModal from './TranslationsModal.svelte'
  import Dropdown from '../../shared/Dropdown.svelte'
  import ValidationMessage from '../../shared/ValidationMessage.svelte'
  import DatePicker from '../../shared/DatePicker.svelte'
  import {staticData} from '../../stores/static'

  export let poem: Poem
  export let stepTo: (e: Event, stepIndex: number) => void
  const languages = staticData.languages

  // Set default language when languages are loaded and poem doesn't have a language yet
  $: if (languages.length > 0 && !poem.language_id) {
    const defaultLanguage = languages.find(l => l.code === lang)
    if (defaultLanguage) {
      poem.language_id = defaultLanguage.id
      poem.language_code = defaultLanguage.code
    }
  }

  let errors: Record<string, string[]> = {}
  let showEndDate = false

  // Helpers for date comparisons with format d/m/Y
  function parseDMY(dateStr?: string): Date | null {
    if (!dateStr) return null
    const parts = dateStr.split('/')
    if (parts.length !== 3) return null
    const [day, month, year] = parts.map(p => parseInt(p, 10))
    if (!day || !month || !year) return null
    return new Date(year, month - 1, day)
  }

  const todayISO = new Date().toISOString().split('T')[0]

  // Keep constraints consistent: written_from <= written_to
  $: if (poem?.written_from && poem?.written_to) {
    const from = parseDMY(poem.written_from)
    const to = parseDMY(poem.written_to)
    if (from && to && to < from) {
      poem.written_to = poem.written_from
    }
  }

  // Ensure the end date picker is visible when a completed date exists (e.g., after navigating away and back)
  $: if (poem?.written_to) {
    showEndDate = true
  }

  export function validate(): boolean {
    if (!poem.handle || poem.handle.trim().length === 0) {
      errors['handle'] = [t.errors.validation['strict-slug']]
      return false
    }
    errors = {}
    return true
  }

  function removeAuthor(author: any): void {
    poem.authors = poem.authors?.filter(a => a.id !== author.id)
  }

  function addTag(event: KeyboardEvent, tag: string): void {
    if (!poem.tags) {
      poem.tags = []
    }

    const trimmedTag = tag.trim()
    if (trimmedTag.length > 0) {
      if (!poem.tags.some(t => t.name === trimmedTag)) {
        poem.tags = [...poem.tags!, {name: trimmedTag} as Tag];
      }
      (event.target as HTMLInputElement).value = ''
    }
  }

  function removeTag(tag: { name: string }): void {
    poem.tags = poem.tags?.filter(t => t.name !== tag.name)
  }

  function openUsersModal(): void {
    modal.show(UsersModal, {props: {poem, onSelect: (user: User) => poem.authors = [user, ...poem.authors]}})
  }

  function onTranslationSelected(t: Poem) {
    poem.translated_poem = t
    poem.translated_poem_id = t.id
  }

  function openTranslationsModal(e: Event): void {
    e.preventDefault()
    modal.show(TranslationsModal, {props: {poem, onSelected: onTranslationSelected}})
  }

  function setLanguageCode() {
    poem.language_code = languages.find(l => l.id == poem.language_id)?.code!
  }


  function removeTranslation(e: Event): void {
    e.preventDefault()
    poem.translated_poem = undefined
    poem.translated_poem_id = undefined
  }

  function onEnterTag(e: KeyboardEvent) {
    if (e.key === 'Enter') {
      addTag(e, (e.target as HTMLInputElement).value);
      (e.target as HTMLInputElement).value = ''
    }
  }
</script>

{#if poem}
  <div class="editor-block">
    <div class="editor-block-container">
      <div class="left-settings">
        <div class="date__creation">
          <div class="date__item single-date">
            <span class="date__label">{t.poem.form.settings.written_on}</span>

            <div class="ui calendar">
              <div class="date-input">
                <DatePicker 
                  bind:value={poem.written_from}
                  placeholder={t.poem.form.settings.select_date}
                  minDate="1400-01-01"
                  maxDate={new Date().toISOString().split('T')[0]}
                  onChange={(date) => poem.written_from = date} />
              </div>
            </div>

            <span class="add-end-date-link" onclick={() => showEndDate = !showEndDate}>
              {#if !showEndDate}
                {t.poem.form.settings.was_writing_several_days}
              {:else}
                {t.poem.form.settings.written_in_one_day}
              {/if}
            </span>
          </div>

          {#if showEndDate}
            <div class="date__item">
              <span class="date__label">{t.poem.form.settings.completed_on}</span>

              <div class="ui calendar">
                <div class="date-input">
                  <DatePicker 
                    bind:value={poem.written_to}
                    placeholder={t.poem.form.settings.select_date}
                    minDate={poem.written_from || "1400-01-01"}
                    maxDate={new Date().toISOString().split('T')[0]}
                    onChange={(date) => poem.written_to = date} />
                </div>
              </div>
            </div>
          {/if}
        </div>

        <div class="grid__item">
          <div class="line">
            <span>{t.poem.form.settings.handle}</span>
            <input type="text" class="poem-handle" required
                   bind:value={poem.handle} placeholder={t.poem.handle} class:error-input={errors.handle}/>

            <ValidationMessage errors={errors.handle}/>
          </div>
        </div>

        <div class="line language">
          <span class="language-label">{t.poem.form.settings.language}:</span>
          <Dropdown bind:value={poem.language_id} autocomplete onChange={setLanguageCode} autoclose
                    class="ui search inline dropdown lang-select" emptyOption={t.languages.prompt}
                    options={languages.sort((a, b) => a.name.localeCompare(b.name)).map(l => ({value: l.id, label: (t.languages as any)[l.code]}))}/>
          
          <span class="poem-type-spacer"></span>
          
          <Dropdown class="ui search inline dropdown poem-type" bind:value={poem.original}
                    options={[{label: t.poem.form.settings.type.original, value: true}, {label: t.poem.form.settings.type.translated, value: false}]}/>
        </div>

        <div class="add__tags">
          <div class="line">
            <span>{t.poem.form.settings.add_tags}</span>
            <input type="text" placeholder={t.poem.form.settings.type_tags}
                   onkeydown={onEnterTag}/>
          </div>
          <div class="added__tags">
            {#each poem.tags || [] as tag}
              <div class="added__tag_item">
                {tag.name}
                <i class="remove icon-p-close" onclick={() => removeTag(tag)} role="button" tabindex="0" onkeydown={(e) => e.key === 'Enter' && removeTag(tag)}></i>
              </div>
            {/each}
          </div>
        </div>
      </div>

      <div class="right-settings">
        <div class="add__authors">
          <div class="small-title">{t.poem.form.settings.authors}</div>
          {#each poem.authors as author}
            <span class="added__author_item">
              <i class="remove icon-p-close" onclick={() => removeAuthor(author)} role="button" tabindex="0" onkeydown={(e) => e.key === 'Enter' && removeAuthor(author)}></i>
              <a class="added__author_circle" title={author.full_name} href="javascript:void(0)" role="button">
                <img src={author.avatar_big_thumgb} alt={author.full_name}/>
              </a>
            </span>
          {/each}
          <span class="add__circle_btn" onclick={openUsersModal} role="button" tabindex="0" onkeydown={(e) => e.key === 'Enter' && openUsersModal()}></span>
        </div>

        {#if !poem.original && !poem.translated_poem_id}
          <a class="add-translation" onclick={openTranslationsModal} href="javascript:void(0)" role="button">
            {t.poem.form.settings.add_translation}
          </a>
        {/if}

        {#if poem.translated_poem?.handle}
          <a href={`/poems/${poem.translated_poem.handle}`}>
            {replaceValues(t.poem.form.settings.translation_to, {title: poem.translated_poem.title})}
          </a>

          <a onclick={removeTranslation} href="javascript:void(0)" class="remove-translation" role="button">{t.poem.form.settings.translation.remove}</a>
        {/if}
      </div>
    </div>
    
    <div class="step-button-wrap">
      <a class="ui button inverted" onclick={(e) => stepTo(e, 2)} href="javascript:void(0)" role="button">
        {t.poem.steps.continue}
      </a>
    </div>
  </div>
{/if}

<style>
  .remove-translation {
    font-weight: bold;
  }

  .add__circle_btn {
    cursor: pointer;
  }
</style>
